/**
 * The home's composer: the shared `MessageInput` with the controls that act
 * before a session exists (file upload, add context, Skills, reasoning effort,
 * mic, send), and the canvas landing's quick actions under it. Send and a
 * quick action both run the dispatcher; this composer never starts a chat.
 *
 * Attachments upload into the private workspace, where the canvas lands too,
 * and their ids ride the first message. A file dropped anywhere on the home
 * attaches here (the layout's OS drop layer), as do in-app drags onto the
 * input; Send waits for uploads the way the legacy home's does.
 */

import { usePrivateWorkspace } from "@/api/workspaces";
import { ComposerSuggestions } from "@/components/ComposerSuggestions";
import { MessageInput } from "@/components/MessageInput";
import { MessageInputControlSet } from "@/constants/message-input";
import { ComposerAttachDropTarget } from "@/dnd/ComposerAttachDropTarget";
import { useRegisterOsFileDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterComposerAttachTarget } from "@/dnd/TransferDndProvider";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import type { UploadTarget } from "@/hooks/useFileRecordUpload";
import { useMessageActions } from "@/hooks/useMessageActions";
import { useReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import type { PostMessageParams, ReferencedEntity } from "@cohesive/api-client";
import { useCallback, useMemo, useState } from "react";
import type { HomeSend } from "./dispatch";
import { homeHeaderPlacement, pickQuickActions } from "./quickActions";

export interface NewHomeComposerProps {
  onSend: (message: HomeSend) => void;
  busy: boolean;
}

export function NewHomeComposer({ onSend, busy }: NewHomeComposerProps) {
  // The canvas lands in the private workspace; until it resolves there is
  // nowhere to create one or to upload into, so Send and attach stay off.
  const privateWorkspace = usePrivateWorkspace();
  const lg = useBreakpoint("lg");
  const [referencedEntities, setReferencedEntities] = useState<
    ReferencedEntity[]
  >([]);
  // The same key as the legacy home composer, so the preference carries over;
  // without one the home starts at high, and the quick actions send the same.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    "composer_reasoning_effort_home",
    "high",
  );
  // Three of the six, drawn once per visit.
  const [quickActions] = useState(() => pickQuickActions());

  const attachTarget = useMemo<UploadTarget | undefined>(
    () =>
      privateWorkspace
        ? { class: "workspace", workspace_id: privateWorkspace.id }
        : undefined,
    [privateWorkspace],
  );
  const {
    uploadedFiles,
    readyFileRecordIds,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
  } = useMessageActions({ surface: "home_composer", attachTarget });
  const attach = attachTarget ? onAttachFile : undefined;

  const onOsFileDrop = useCallback(
    (files: File[] | null) => attach?.(files, { method: "drop_os" }),
    [attach],
  );
  useRegisterOsFileDrop(attach && onOsFileDrop);
  // No session to copy into before the send: in-app drags attach workspace
  // files as references.
  useRegisterComposerAttachTarget("home", {
    ensureSession: async () => null,
    onAttached: (records) =>
      onAttachFileRecords(records, { method: "drop_in_app" }),
  });

  const handleSend = async (params: PostMessageParams) => {
    const content = params.content?.trim() ?? "";
    if (!content && readyFileRecordIds.length === 0) return;
    onSend({
      content,
      referenced_entities: referencedEntities,
      reasoning_effort: params.reasoning_effort,
      ...(readyFileRecordIds.length
        ? { file_record_ids: readyFileRecordIds }
        : {}),
    });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <ComposerAttachDropTarget scopeKey="home" className="w-full rounded-xl">
        <div className="w-full rounded-xl shadow-elev-1">
          <MessageInput
            attachedFiles={uploadedFiles}
            onAttachFile={attach}
            onAttachFileRecords={onAttachFileRecords}
            onRemoveFile={onRemoveFile}
            filesWorkspaceId={privateWorkspace?.id}
            controlSet={MessageInputControlSet.NEW_HOME}
            onSend={handleSend}
            onReferencedEntitiesChange={setReferencedEntities}
            sendButtonDisabled={busy || !privateWorkspace}
            autoFocus={lg}
            location="main"
            agentReasoningEffort={reasoningEffort}
            onAgentReasoningEffortChange={setReasoningEffort}
            classNames={{ container: "w-full", editor: "min-h-[72px]" }}
          />
        </div>
      </ComposerAttachDropTarget>
      <ComposerSuggestions
        suggestions={quickActions}
        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        // Three in a row from lg; two where three do not fit, never an
        // orphan; phones stack them.
        cardClassName={(index) => (index === 2 ? "sm:max-lg:hidden" : "")}
        disabled={busy || !privateWorkspace}
        onSelect={(suggestion) =>
          onSend({
            content: suggestion.prompt,
            referenced_entities: [],
            reasoning_effort: reasoningEffort,
            header: homeHeaderPlacement(suggestion.id),
          })
        }
      />
    </div>
  );
}
